import {
  ABILITY_KEYS,
  ARMORS,
  CONDITIONS,
  CR_TABLE,
  DAMAGE_TYPES,
  SIZES,
  SKILLS,
  STATBLOCK_DE,
  abilityMod,
  abilityScore,
  commonerStatblock,
  formatMod,
  formatStatblock,
  normalizeStatblock,
  summarizeStatblock,
  toMonsterFile,
  type AbilityKey,
  type ResolvedLinkTarget,
  type Statblock,
  type StatblockEntry,
  type StatblockEntryList,
} from '@kartothek/shared';
import { useRef, useState, type ReactNode } from 'react';
import { t } from '../i18n';
import { MarkdownBody } from './MarkdownView';

const D = STATBLOCK_DE;

interface RenderProps {
  cid: string;
  links: Record<string, ResolvedLinkTarget>;
  folder: string;
  isGameMaster: boolean;
}

function Rule() {
  return <svg className="sb-rule" height="5" width="100%" preserveAspectRatio="none" viewBox="0 0 400 5" aria-hidden><polyline points="0,0 400,2.5 0,5" /></svg>;
}

function Entry({ entry, r }: { entry: StatblockEntry; r: RenderProps }) {
  const md = entry.name ? `***${entry.name.replace(/\*/g, '\\*')}.*** ${entry.desc}` : entry.desc;
  return <MarkdownBody cid={r.cid} body={md} links={r.links} folder={r.folder} depth={2} chain={[]} isGameMaster={r.isGameMaster} preview />;
}

function Text({ text, r }: { text: string; r: RenderProps }) {
  return <MarkdownBody cid={r.cid} body={text} links={r.links} folder={r.folder} depth={2} chain={[]} isGameMaster={r.isGameMaster} preview />;
}

/** Statblock im Stil des Monster-Makers (5e 2014) bzw. des Monster Manual 2024. */
export function StatblockView(props: RenderProps & { statblock: Statblock; fallbackName?: string; compact?: boolean }) {
  const f = formatStatblock(props.statblock);
  const name = f.name || props.fallbackName || '';
  return (
    <div className={`statblock sb-${f.format}${props.compact ? ' sb-compact' : ''}`}>
      <div className="sb-head">
        <h2 className="sb-name">{name}</h2>
        <div className="sb-sub">{f.subtitle}</div>
      </div>
      <Rule />
      {f.format === '2014' ? (
        <>
          <div className="sb-prop">
            <b>{D.labels.ac}</b> {f.acText}
          </div>
          <div className="sb-prop">
            <b>{D.labels.hp}</b> {f.hpText}
          </div>
          <div className="sb-prop">
            <b>{D.labels.speed}</b> {f.speedText}
          </div>
          <Rule />
          <div className="sb-abilities">
            {f.abilities.map((a) => (
              <div key={a.key}>
                <b>{a.label}</b>
                <span>
                  {a.score} ({a.mod})
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="sb-prop">
            <b>{D.labels.acShort}</b> {f.acText} <span className="sb-gap" /> <b>{D.labels.initiative}</b> {f.initiativeText}
          </div>
          <div className="sb-prop">
            <b>{D.labels.hpShort}</b> {f.hpText}
          </div>
          <div className="sb-prop">
            <b>{D.labels.speed}</b> {f.speedText}
          </div>
          <table className="sb-abilities-2024">
            <thead>
              <tr>
                {[0, 1, 2].map((i) => (
                  <th key={i} colSpan={4}>
                    <span />
                    <span>{D.labels.mod}</span>
                    <span>{D.labels.save}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[0, 1].map((row) => (
                <tr key={row}>
                  {f.abilities.slice(row * 3, row * 3 + 3).map((a) => (
                    <td key={a.key} colSpan={4} className={a.proficient ? 'prof' : undefined}>
                      <b>{a.label}</b>
                      <span>{a.score}</span>
                      <span>{a.mod}</span>
                      <span>{a.save}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <Rule />
      {f.lines.map((l) => (
        <div key={l.label} className="sb-prop">
          <b>{l.label}</b> {l.text}
        </div>
      ))}
      {f.sections.length > 0 && <Rule />}
      {f.sections.map((s) => (
        <div key={s.key} className="sb-section">
          {s.title && <h3 className="sb-section-title">{s.title}</h3>}
          {s.intro && <Text text={s.intro} r={props} />}
          {s.entries.map((e, i) => (
            <Entry key={i} entry={e} r={props} />
          ))}
          {s.outro && <Text text={s.outro} r={props} />}
        </div>
      ))}
    </div>
  );
}

// ---------- Editor ----------

function NumberInput(props: { value: number | undefined; onChange: (v: number | undefined) => void; min?: number; placeholder?: string; width?: number }) {
  return (
    <input
      type="number"
      className="sb-num"
      style={props.width ? { width: props.width } : undefined}
      value={props.value ?? ''}
      min={props.min}
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
    />
  );
}

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`sb-field${wide ? ' wide' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function EntryList(props: { title: string; entries: StatblockEntry[] | undefined; onChange: (e: StatblockEntry[]) => void }) {
  const list = props.entries ?? [];
  const set = (i: number, patch: Partial<StatblockEntry>) => props.onChange(list.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const move = (i: number, d: number) => {
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x!);
    props.onChange(next);
  };
  return (
    <div className="sb-entries">
      <div className="sb-entries-head">
        <b>{props.title}</b>
        <button type="button" onClick={() => props.onChange([...list, { name: '', desc: '' }])}>
          + {t('statblock.addEntry')}
        </button>
      </div>
      {list.map((e, i) => (
        <div key={i} className="sb-entry-edit">
          <div className="sb-entry-row">
            <input placeholder={t('common.name')} value={e.name} onChange={(ev) => set(i, { name: ev.target.value })} />
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} title={t('statblock.up')}>
              ↑
            </button>
            <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} title={t('statblock.down')}>
              ↓
            </button>
            <button type="button" className="danger" onClick={() => props.onChange(list.filter((_, j) => j !== i))} title={t('common.delete')}>
              ×
            </button>
          </div>
          <textarea rows={2} placeholder={t('statblock.descHint')} value={e.desc} onChange={(ev) => set(i, { desc: ev.target.value })} />
        </div>
      ))}
    </div>
  );
}

type DmgState = '' | 'v' | 'r' | 'i';

/** Formular für alle Werte des Statblocks, mit Live-Vorschau. */
export function StatblockEditor(
  props: RenderProps & { statblock: Statblock; noteTitle: string; onChange: (sb: Statblock | undefined) => void },
) {
  const sb = props.statblock;
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Neu aufbauen nach Laden/Zurücksetzen (Felder mit defaultValue)
  const [rev, setRev] = useState(0);
  const replace = (next: Statblock | undefined) => {
    props.onChange(next);
    setRev((r) => r + 1);
  };
  const set = (patch: Partial<Statblock>) => props.onChange(normalizeStatblock({ ...sb, ...patch }));
  const summary = summarizeStatblock(sb);

  const loadFile = async (file: File) => {
    try {
      const json = JSON.parse(await file.text());
      replace(normalizeStatblock(json));
      setError(null);
    } catch {
      setError(t('statblock.invalidFile'));
    }
  };

  const download = () => {
    const blob = new Blob([JSON.stringify(toMonsterFile({ ...sb, name: sb.name || props.noteTitle }))], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(sb.name || props.noteTitle).replace(/[\\/:*?"<>|]/g, '_')}.monster`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const skillState = (name: string): '' | 'p' | 'e' => {
    const s = sb.skills?.find((x) => x.name === name);
    return !s ? '' : /ex/i.test(s.note ?? '') ? 'e' : 'p';
  };
  const setSkill = (name: string, stat: AbilityKey, state: '' | 'p' | 'e') => {
    const rest = (sb.skills ?? []).filter((x) => x.name !== name);
    const next = state === '' ? rest : [...rest, state === 'e' ? { name, stat, note: ' (ex)' } : { name, stat }];
    next.sort((a, b) => SKILLS.findIndex((s) => s.name === a.name) - SKILLS.findIndex((s) => s.name === b.name));
    set({ skills: next });
  };
  const dmgState = (name: string): DmgState => sb.damagetypes?.find((d) => d.name === name)?.type ?? '';
  const setDmg = (name: string, type: DmgState) => {
    const rest = (sb.damagetypes ?? []).filter((d) => d.name !== name);
    set({ damagetypes: type ? [...rest, { name, type }] : rest });
  };
  const special = (type: 'v' | 'r' | 'i') =>
    (sb.specialdamage ?? [])
      .filter((d) => d.type === type)
      .map((d) => d.name)
      .join('; ');
  const setSpecial = (type: 'v' | 'r' | 'i', text: string) => {
    const rest = (sb.specialdamage ?? []).filter((d) => d.type !== type);
    const items = text
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name, type }));
    set({ specialdamage: [...rest, ...items] });
  };
  const hasCond = (name: string) => !!sb.conditions?.some((c) => c.name === name);
  const toggleCond = (name: string) =>
    set({ conditions: hasCond(name) ? sb.conditions!.filter((c) => c.name !== name) : [...(sb.conditions ?? []), { name }] });
  const langText = (speaks: boolean) =>
    (sb.languages ?? [])
      .filter((l) => l.speaks === speaks)
      .map((l) => l.name)
      .join(', ');
  const setLang = (speaks: boolean, text: string) => {
    const keep = (sb.languages ?? []).filter((l) => l.speaks !== speaks);
    const items = text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name, speaks }));
    set({ languages: speaks ? [...items, ...keep] : [...keep, ...items] });
  };
  const entryList = (key: StatblockEntryList, title: string) => (
    <EntryList title={title} entries={sb[key]} onChange={(e) => set({ [key]: e } as Partial<Statblock>)} />
  );

  return (
    <div className="sb-editor">
      <div className="sb-editor-bar">
        <span className="muted small">
          {D.labels.acShort} {summary.ac} · {D.labels.hpShort} {summary.hp} · {D.labels.initiative} {formatMod(summary.initiative)}
        </span>
        <span className="grow" />
        <select value={sb.format ?? '2014'} onChange={(e) => set({ format: e.target.value === '2024' ? '2024' : undefined })} title={t('statblock.format')}>
          <option value="2014">5e 2014</option>
          <option value="2024">5e 2024</option>
        </select>
        <button type="button" onClick={() => fileRef.current?.click()}>
          {t('statblock.load')}
        </button>
        <button type="button" onClick={download}>
          {t('statblock.download')}
        </button>
        <button type="button" onClick={() => confirm(t('statblock.resetConfirm')) && replace(commonerStatblock(props.noteTitle))}>
          {t('statblock.reset')}
        </button>
        <button type="button" className="danger" onClick={() => confirm(t('statblock.removeConfirm')) && props.onChange(undefined)}>
          {t('statblock.remove')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".monster,.json,application/json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void loadFile(file);
            e.target.value = '';
          }}
        />
      </div>
      {error && <div className="error">{error}</div>}
      <div className="sb-editor-grid">
        <div className="sb-form" key={rev}>
          <fieldset>
            <legend>{t('statblock.general')}</legend>
            <Field label={t('common.name')} wide>
              <input value={sb.name ?? ''} placeholder={props.noteTitle} onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field label={t('statblock.size')}>
              <select value={sb.size ?? 'medium'} onChange={(e) => set({ size: e.target.value })}>
                {SIZES.map((s) => (
                  <option key={s} value={s}>
                    {D.sizeNames[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('statblock.type')}>
              <input value={sb.type ?? ''} onChange={(e) => set({ type: e.target.value })} />
            </Field>
            <Field label={t('statblock.tag')}>
              <input value={sb.tag ?? ''} onChange={(e) => set({ tag: e.target.value })} />
            </Field>
            <Field label={t('statblock.alignment')}>
              <input value={sb.alignment ?? ''} onChange={(e) => set({ alignment: e.target.value })} />
            </Field>
          </fieldset>

          <fieldset>
            <legend>{t('statblock.defense')}</legend>
            <Field label={D.labels.ac}>
              <select value={sb.armorName ?? 'none'} onChange={(e) => set({ armorName: e.target.value })}>
                {Object.keys(ARMORS).map((a) => (
                  <option key={a} value={a}>
                    {D.armors[a] ?? a}
                  </option>
                ))}
              </select>
            </Field>
            {sb.armorName === 'natural armor' && (
              <Field label={t('statblock.naturalBonus')}>
                <NumberInput value={sb.natArmorBonus} onChange={(v) => set({ natArmorBonus: v })} />
              </Field>
            )}
            {sb.armorName === 'other' && (
              <Field label={t('statblock.armorText')}>
                <input value={sb.otherArmorDesc ?? ''} placeholder="15 (Schuppen)" onChange={(e) => set({ otherArmorDesc: e.target.value })} />
              </Field>
            )}
            <label className="sb-check">
              <input type="checkbox" checked={(sb.shieldBonus ?? 0) > 0} onChange={(e) => set({ shieldBonus: e.target.checked ? 2 : 0 })} />
              {D.labels.shield}
            </label>
            <Field label={t('statblock.hitDice')}>
              <NumberInput value={sb.hitDice} min={1} onChange={(v) => set({ hitDice: v })} />
            </Field>
            <label className="sb-check">
              <input type="checkbox" checked={!!sb.customHP} onChange={(e) => set({ customHP: e.target.checked, hpText: sb.hpText ?? `${summary.hp}` })} />
              {t('statblock.customHp')}
            </label>
            {sb.customHP && (
              <Field label={D.labels.hp}>
                <input value={sb.hpText ?? ''} placeholder="27 (5d8 + 5)" onChange={(e) => set({ hpText: e.target.value })} />
              </Field>
            )}
          </fieldset>

          <fieldset>
            <legend>{D.labels.speed}</legend>
            <Field label={t('statblock.walk')}>
              <NumberInput value={sb.speed} onChange={(v) => set({ speed: v })} />
            </Field>
            <Field label={D.speeds.burrow}>
              <NumberInput value={sb.burrowSpeed} onChange={(v) => set({ burrowSpeed: v })} />
            </Field>
            <Field label={D.speeds.climb}>
              <NumberInput value={sb.climbSpeed} onChange={(v) => set({ climbSpeed: v })} />
            </Field>
            <Field label={D.speeds.fly}>
              <NumberInput value={sb.flySpeed} onChange={(v) => set({ flySpeed: v })} />
            </Field>
            <Field label={D.speeds.swim}>
              <NumberInput value={sb.swimSpeed} onChange={(v) => set({ swimSpeed: v })} />
            </Field>
            {!!sb.flySpeed && (
              <label className="sb-check">
                <input type="checkbox" checked={!!sb.hover} onChange={(e) => set({ hover: e.target.checked })} />
                {D.labels.hover}
              </label>
            )}
          </fieldset>

          <fieldset>
            <legend>{t('statblock.abilities')}</legend>
            <div className="sb-ability-edit">
              {ABILITY_KEYS.map((k) => {
                const score = abilityScore(sb, k);
                const prof = !!sb.sthrows?.some((s) => s.name === k);
                return (
                  <div key={k}>
                    <b title={D.abilityNames[k]}>{D.abilities[k]}</b>
                    <NumberInput value={sb[`${k}Points`]} min={1} placeholder="10" onChange={(v) => set({ [`${k}Points`]: v } as Partial<Statblock>)} />
                    <span className="muted small">{formatMod(abilityMod(score))}</span>
                    <label className="small" title={D.labels.saves}>
                      <input
                        type="checkbox"
                        checked={prof}
                        onChange={() =>
                          set({ sthrows: prof ? sb.sthrows!.filter((s) => s.name !== k) : [...(sb.sthrows ?? []), { name: k }] })
                        }
                      />
                      {D.labels.save}
                    </label>
                  </div>
                );
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend>{D.labels.skills}</legend>
            <div className="sb-skill-grid">
              {SKILLS.map((s) => (
                <label key={s.name}>
                  <select value={skillState(s.name)} onChange={(e) => setSkill(s.name, s.stat, e.target.value as '' | 'p' | 'e')}>
                    <option value="">–</option>
                    <option value="p">{t('statblock.proficient')}</option>
                    <option value="e">{t('statblock.expertise')}</option>
                  </select>
                  {D.skills[s.name]} <span className="muted small">({D.abilities[s.stat]})</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend>{t('statblock.damage')}</legend>
            <div className="sb-skill-grid">
              {DAMAGE_TYPES.map((d) => (
                <label key={d}>
                  <select value={dmgState(d)} onChange={(e) => setDmg(d, e.target.value as DmgState)}>
                    <option value="">–</option>
                    <option value="v">{t('statblock.vulnerable')}</option>
                    <option value="r">{t('statblock.resistant')}</option>
                    <option value="i">{t('statblock.immune')}</option>
                  </select>
                  {D.damage[d]}
                </label>
              ))}
            </div>
            <Field label={`${t('statblock.resistant')} (${t('statblock.special')})`} wide>
              <input defaultValue={special('r')} placeholder={t('statblock.specialHint')} onBlur={(e) => setSpecial('r', e.target.value)} />
            </Field>
            <Field label={`${t('statblock.immune')} (${t('statblock.special')})`} wide>
              <input defaultValue={special('i')} placeholder={t('statblock.specialHint')} onBlur={(e) => setSpecial('i', e.target.value)} />
            </Field>
            <div className="sb-conditions">
              <span className="small">{D.labels.conditionImmunities}:</span>
              {CONDITIONS.map((c) => (
                <label key={c} className={`chip-toggle${hasCond(c) ? ' on' : ''}`}>
                  <input type="checkbox" checked={hasCond(c)} onChange={() => toggleCond(c)} />
                  {D.conditions[c]}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend>{D.labels.senses}</legend>
            <Field label={D.senses.darkvision}>
              <NumberInput value={sb.darkvision} onChange={(v) => set({ darkvision: v })} />
            </Field>
            <Field label={D.senses.blindsight}>
              <NumberInput value={sb.blindsight} onChange={(v) => set({ blindsight: v })} />
            </Field>
            <Field label={D.senses.tremorsense}>
              <NumberInput value={sb.tremorsense} onChange={(v) => set({ tremorsense: v })} />
            </Field>
            <Field label={D.senses.truesight}>
              <NumberInput value={sb.truesight} onChange={(v) => set({ truesight: v })} />
            </Field>
            {!!sb.blindsight && (
              <label className="sb-check">
                <input type="checkbox" checked={!!sb.blind} onChange={(e) => set({ blind: e.target.checked })} />
                {D.labels.blindBeyond}
              </label>
            )}
          </fieldset>

          <fieldset>
            <legend>{D.labels.languages}</legend>
            <Field label={t('statblock.speaks')} wide>
              <input defaultValue={langText(true)} placeholder="Gemeinsprache, Drakonisch" onBlur={(e) => setLang(true, e.target.value)} />
            </Field>
            <Field label={t('statblock.understandsOnly')} wide>
              <input defaultValue={langText(false)} onBlur={(e) => setLang(false, e.target.value)} />
            </Field>
            <Field label={D.labels.telepathy}>
              <NumberInput value={sb.telepathy} onChange={(v) => set({ telepathy: v })} />
            </Field>
          </fieldset>

          <fieldset>
            <legend>{D.labels.challenge}</legend>
            <Field label={D.labels.challengeShort}>
              <select value={sb.cr ?? ''} onChange={(e) => set({ cr: e.target.value || undefined })}>
                <option value="">{t('statblock.noCr')}</option>
                {Object.entries(CR_TABLE).map(([cr, row]) => (
                  <option key={cr} value={cr}>
                    {cr} ({row.xp.toLocaleString('de-DE')} {D.labels.xp})
                  </option>
                ))}
                <option value="*">{t('statblock.customCr')}</option>
              </select>
            </Field>
            {(sb.cr === '*' || !sb.cr) && (
              <Field label={t('statblock.profBonus')}>
                <NumberInput value={sb.customProf} placeholder="2" onChange={(v) => set({ customProf: v })} />
              </Field>
            )}
            {sb.cr === '*' && (
              <Field label={t('statblock.crText')}>
                <input value={sb.customCr ?? ''} onChange={(e) => set({ customCr: e.target.value })} />
              </Field>
            )}
          </fieldset>

          {entryList('abilities', D.labels.traits)}
          {entryList('actions', D.labels.actions)}
          {entryList('bonusActions', D.labels.bonusActions)}
          {entryList('reactions', D.labels.reactions)}
          <label className="sb-check">
            <input type="checkbox" checked={!!sb.isLegendary} onChange={(e) => set({ isLegendary: e.target.checked })} />
            {D.labels.legendaries}
          </label>
          {sb.isLegendary && (
            <>
              <textarea
                rows={2}
                value={sb.legendariesDescription ?? ''}
                placeholder={t('statblock.legendaryIntro')}
                onChange={(e) => set({ legendariesDescription: e.target.value })}
              />
              {entryList('legendaries', D.labels.legendaries)}
            </>
          )}
          <label className="sb-check">
            <input type="checkbox" checked={!!sb.isLair} onChange={(e) => set({ isLair: e.target.checked })} />
            {D.labels.lairs}
          </label>
          {sb.isLair && (
            <>
              <textarea rows={2} value={sb.lairDescription ?? ''} onChange={(e) => set({ lairDescription: e.target.value })} />
              {entryList('lairs', D.labels.lairs)}
            </>
          )}
        </div>
        <div className="sb-preview">
          <StatblockView {...props} fallbackName={props.noteTitle} />
        </div>
      </div>
    </div>
  );
}
